import { useCallback, useMemo, useState } from "react"

import { type ExternalBuyLink } from "@forento/core/models/product"

export type ExternalBuyLinkState = ReturnType<typeof useExternalBuyLink>

export const externalBuyLabelMaxLength = 60

// The result is memoized so pages can list it as an effect dependency.
export default function useExternalBuyLink(initial: ExternalBuyLink | null) {
	const [url, setUrl] = useState(initial?.url ?? "")
	const [label, setLabel] = useState(initial?.label ?? "")
	const initialUrl = initial?.url ?? null
	const initialLabel = initial?.label ?? null
	const reset = useCallback((link: ExternalBuyLink | null) => {
		setUrl(link?.url ?? "")
		setLabel(link?.label ?? "")
	}, [])

	return useMemo(() => {
		const trimmedUrl = url.trim()
		const value: ExternalBuyLink | null =
			trimmedUrl.length > 0 ? { url: trimmedUrl, label: label.trim() || null } : null
		return {
			url,
			setUrl,
			label,
			setLabel,
			value,
			isModified: (value?.url ?? null) !== initialUrl || (value?.label ?? null) !== initialLabel,
			error:
				trimmedUrl.length > 0 && !isHttpUrl(trimmedUrl)
					? "The buy link must be a full web address starting with https://."
					: null,
			reset,
		}
	}, [url, label, initialUrl, initialLabel, reset])
}

function isHttpUrl(value: string): boolean {
	try {
		const { protocol } = new URL(value)
		return protocol === "https:" || protocol === "http:"
	} catch {
		return false
	}
}
